// Define a structural interface for the captured query parameters
interface CapturedParameter {
    name: string;
    value: string;
}

export class AffiliateCapture {
    // Define cookie names as constants
    private readonly DOMESTIC_COOKIE = 'WFDomesticAffiliate';
    private readonly OVHC_COOKIE = 'WFOVHCAffiliate';

    constructor() {
        this.captureDetails();
    }

    /**
     * Parses the current window location search string and extracts relevant parameters.
     */
    private getAllQueryVars(): CapturedParameter[] {
        const query = window.location.search;
        if (!query) {
            return [];
        }

        // Remove the leading '?' if it exists
        const queryString = query.startsWith('?') ? query.slice(1) : query;

        return queryString.split('&').map(param => {
            const [key, value] = param.split('=');
            return {
                name: key.toLowerCase(),
                value: value ? decodeURIComponent(value.replace(/\+/g, ' ')) : ''
            };
        });
    }

    /**
     * Captures the specific agent IDs from the URL and saves them to their respective cookies.
     */
    private captureDetails(): void {
        const queryVars = this.getAllQueryVars();
        if (queryVars.length === 0) {
            return;
        }

        // Process each target parameter individually using native browser logic
        queryVars.forEach(qv => {
            if (qv.value === '') {
                return;
            }

            if (qv.name === 'agentid') {
                this.setCookie(this.DOMESTIC_COOKIE, qv.value, 7);
            } else if (qv.name === 'osagentid') {
                this.setCookie(this.OVHC_COOKIE, qv.value, 7);
            }
        });
    }

    /**
     * Native browser utility to write a cookie.
     */
    private setCookie(name: string, value: string, daysToExpire: number): void {
        const date = new Date();
        date.setTime(date.getTime() + (daysToExpire * 24 * 60 * 60 * 1000));
        const expires = `expires=${date.toUTCString()}`;
        // path=/ ensures the cookie is accessible across the entire website
        document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax`;
    }

    /**
     * Native browser utility to read a cookie value by its name.
     */
    private getCookie(name: string): string {
        const nameEQ = `${name}=`;
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1, c.length);
            }
            if (c.indexOf(nameEQ) === 0) {
                return decodeURIComponent(c.substring(nameEQ.length, c.length));
            }
        }
        return '';
    }
    /**
     * Manually sets the Domestic Agent ID cookie.
     * @param value The agent ID value to store.
     */
    public setDomesticAgentId(value: string): void {
        this.setCookie(this.DOMESTIC_COOKIE, value, this.COOKIE_EXPIRY_DAYS);
    }

    /**
     * Manually sets the OVHC Agent ID cookie.
     * @param value The agent ID value to store.
     */
    public setOvhcAgentId(value: string): void {
        this.setCookie(this.OVHC_COOKIE, value, this.COOKIE_EXPIRY_DAYS);
    }
    
    /**
     * Retrieves the captured Domestic Agent ID from the cookie.
     */
    public getCapturedDomesticAgentId(): string {
        return this.getCookie(this.DOMESTIC_COOKIE);
    }

    /**
     * Retrieves the captured OVHC Agent ID from the cookie.
     */
    public getCapturedOvhcAgentId(): string {
        return this.getCookie(this.OVHC_COOKIE);
    }
}

window.WF = window.WF || {};
window.WF.AffiliateCapture = new AffiliateCapture();